import { Button } from '@components/Button/';
import AspectBox from '@components/Layout/AspectBox';
import { VerticalLineSeparator } from '@components/MiscComponents/VerticalLineSeparator';
import RichText from '@components/RichText';
import { color, query, space } from '@design/templateFns';
import { FC } from 'react';
import styled from 'styled-components';
import { Eyebrow, Header, Text } from '../styles';

export interface BigPictureCtaProps {
  header: string;
  eyebrow?: string;
  richText?: any;
  ctaText: string;
  ctaUrl: string;
  img: string;
  img2?: string;
  buttonExternalOption?: boolean;
  backgroundColor?: boolean | string;
}

const BigPictureCta: FC<BigPictureCtaProps> = ({
  header,
  eyebrow,
  richText,
  ctaText,
  ctaUrl,
  img,
  img2,
  buttonExternalOption,
  backgroundColor,
}) => {
  return (
    <Container $backgroundColor={backgroundColor} data-testid="big-picture-section">
      <GridTopContainer>
        {img && (
          <>
            <ImageContainer>
              <ImageBox
                data-testid="big-picture-top-img"
                $ratio={[1.78, 2.4]}
                $img={img}
              ></ImageBox>
            </ImageContainer>
          </>
        )}

        <ContentContainer>
          {eyebrow && <Eyebrow data-testid="big-picture-eyebrow">{eyebrow}</Eyebrow>}
          {header && <Header data-testid="big-picture-header">{header}</Header>}
          {richText && (
            <Text>
              <RichText dataTestId="big-picture-description" richText={richText} />
            </Text>
          )}
          {ctaUrl && ctaText && (
            <Button
              arrow={true}
              dataTestId="big-picture-cta-link"
              variant={'tertiary'}
              url={ctaUrl}
              label={ctaText}
              target={buttonExternalOption}
            />
          )}
        </ContentContainer>
      </GridTopContainer>
      {img2 && (
        <>
          <StyledVerticalLineSeparator />
          <GridBottomContainer>
            <ImageBox2
              data-testid="big-picture-main-img"
              $ratio={[1.78, 2.4]}
              $img={img2}
            ></ImageBox2>
          </GridBottomContainer>
        </>
      )}
    </Container>
  );
};

export default BigPictureCta;

export const Container = styled.div<{ $backgroundColor?: boolean | string }>`
  display: grid;
  padding: ${space('x10')} ${space('xl')} ${space('x7')};
  background-color: ${({ $backgroundColor }) =>
    $backgroundColor ? color('secondaryBg') : color('bg')};

  @media (min-width: 375px) {
    width: 100vw;
    margin-left: calc(-50vw + 50%);
    padding: ${space('x6')} 20px;
    box-sizing: border-box;
  }
  @media (${query.atLeast('desktop')}) {
    width: 100vw;
    margin-left: calc(-50vw + 50%);
    padding: ${space('x6')} 20px;
    box-sizing: border-box; /* Include padding in width calculation */
  }
  @media (${query.atLeast('max')}) {
    width: 1440px;
    margin-left: calc(-720px + 50%);
  }
`;

export const GridTopContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 500px;
  margin: auto;
  gap: 50px;
`;

export const GridBottomContainer = styled.div`
  display: flex;
  gap: 50px;
  flex-direction: column;
`;

export const StyledVerticalLineSeparator = styled(VerticalLineSeparator)`
  margin: 0 auto;

  @media screen and (${query.below('desktop')}) {
    display: none;
  }
`;

export const ImageContainer = styled.div`
  width: 191px;
  margin: auto;

  @media screen and (${query.atLeast('tablet')}) {
    width: 306px;
  }
`;

export const ContentContainer = styled.div<{ $reverse?: boolean }>`
  grid-row: 2;
  text-align: center;

  @media screen and (${query.atLeast('tablet')}) {
    margin-top: 0;
    grid-row: ${({ $reverse }) => ($reverse ? 1 : 'unset')};
  }

  & > a {
    margin-bottom: ${space('x7')};
  }
`;

export const ImageBox = styled(AspectBox).attrs({ $ratio: [1.78, 2.4] })<{
  $img: string;
}>`
  width: 100%;
  height: 100%;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center center;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: ${({ $img }) => `url(${$img})`};
`;

const ImageBox2 = styled(ImageBox)`
  @media screen and (${query.atLeast('tablet')}) {
    margin-top: ${space('x7')};
  }
`;
